/**
 * Logo width calculations based on CSS variables and aspect ratio
 *
 * CSS Variables used:
 * --space-1 = 8px (padding vertical)
 * --space-2 = 16px (padding horizontal, gap between logos)
 * --space-6 = 48px (small logo inner height)
 * --space-7 = 56px (medium logo inner height)
 * --space-8 = 64px (large logo inner height)
 *
 * Logo structure:
 * - padding: 8px 16px (var(--space-1) var(--space-2))
 * - inner height: varies by size (small: 48px, medium: 56px, large: 64px)
 * - aspect ratio: 16/9
 * - inner width: height * (16/9)
 * - total width: inner_width + (16 * 2)
 */

type LogoSize = "small" | "medium" | "large";

const LOGO_INNER_HEIGHTS: Record<LogoSize, number> = {
  small: 48,  // var(--space-6)
  medium: 56, // var(--space-7)
  large: 64,  // var(--space-8)
};

const LOGO_ASPECT_RATIO = 16 / 9;
const LOGO_PADDING_HORIZONTAL = 16; // var(--space-2) on each side
const SPACE_BETWEEN = 16; // var(--space-2) gap between logos

// Safety margin to account for browser rendering differences, box-sizing, and rounding
const SAFETY_MARGIN = 1.15; // 15% buffer

// Calculate logo width for a given size: (height * aspect ratio) + (padding * 2)
const getLogoWidth = (size: LogoSize = "medium"): number => {
  const height = LOGO_INNER_HEIGHTS[size];
  return height * LOGO_ASPECT_RATIO + LOGO_PADDING_HORIZONTAL * 2;
};

/**
 * Calculate total width required for all logos including spacing
 * @param logoCount - Number of logos to display
 * @param size - Logo size variant
 * @returns Total width in pixels
 */
export const calculateTotalLogosWidth = (logoCount: number, size: LogoSize = "medium"): number => {
  if (logoCount === 0) return 0;

  const logoWidth = getLogoWidth(size);
  if (logoCount === 1) return logoWidth;

  // Total width = (logo width × count) + (spacing × (count - 1))
  return logoWidth * logoCount + SPACE_BETWEEN * (logoCount - 1);
};

/**
 * Check if all logos fit within the container width
 * @param logoCount - Number of logos
 * @param containerWidth - Available width in pixels
 * @param size - Logo size variant
 * @returns true if logos fit, false otherwise
 */
export const doLogosFit = (
  logoCount: number,
  containerWidth: number,
  size: LogoSize = "medium"
): boolean => {
  if (containerWidth === 0) return false; // Not measured yet
  if (logoCount === 0) return true; // No logos always fit

  const totalWidth = calculateTotalLogosWidth(logoCount, size);
  // Apply safety margin to account for rendering variations
  return totalWidth * SAFETY_MARGIN <= containerWidth;
};
